import {HttpHeaders, HttpResponse} from '@angular/common/http';
import {ActivatedRouteSnapshot} from '@angular/router';
import {GenericLinkedResource, GenericObjectTypeInfo} from '@catcore/types/base';
import {labels} from '@catcore/utils/json-schema/type-label-mapping';
import {Resource} from '@catfeatures/types';
import {LinkItem} from '@catui/types';
import {isNullOrUndefined} from 'util';

/**
 * Looks for the value of a given parameter name and route-snapshot, up to the root-route
 *
 * @param {string} paramName
 * @param {ActivatedRouteSnapshot} firstRoute
 * @returns {any}
 */
export const findValueFor = (paramName: string, firstRoute): any => {
  let paramValue = firstRoute.paramMap.get(paramName);
  let routeSnap = firstRoute.firstChild;

  while (paramValue === null && routeSnap !== null) {
    paramValue = routeSnap.paramMap.get(paramName);
    routeSnap = routeSnap.firstChild;
  }

  return paramValue;
};

/**
 * This method returns the parental route by the given route.
 *
 * @param route, the actual given route for the request
 * @returns {any}, the parental route
 */
export const resolveToRoot = (route) => {
  let parentRoute = route;

  while (parentRoute.parent !== null) {
    parentRoute = parentRoute.parent;
  }

  return parentRoute;
};

/**
 * This method returns the requested route parameter by the given route.
 * The return value type can be influenced by using the callback parameter.
 * @param {string} param the name of teh requested parameter
 *
 * @param route th activated route
 * @param {(value) => any} callback
 * @returns {any} the value of the requested parameter
 */
export const retrieveParam = (param: string, route, callback = (value) => {
  return value;
}) => {
  let result;
  let currentRoute = route;

  do {
    result = currentRoute.paramMap.get(param);
    currentRoute = currentRoute.firstChild;
  } while (result === null && currentRoute !== null);

  return callback(result);
};

/**
 * This method returns the requested query parameter by the given route.
 * The return value type can be influenced by using the callback parameter.
 *
 * @param {string} param the name of teh requested parameter
 * @param route th activated route
 * @param {(value) => any} callback
 * @returns {any} the value of the requested parameter
 */
export const retrieveQueryParam = (param: string, route, callback = (value) => {
  return value;
}) => {
  let result;
  let currentRoute = route;

  do {
    result = currentRoute.queryParamMap.get(param);
    currentRoute = currentRoute.firstChild;
  } while (result === null && currentRoute !== null);

  return callback(result);
};

/**
 * Create and returns a key/value Object from a provided Map-object
 *
 * @type {(mapValues: Map<any, any>) => any}
 */
export const mapToObj = (mapValues: Map<any, any>): any => {
  const result = {};
  mapValues.forEach((objectValue, objectKey) => {
    result[objectKey] = objectValue;
  });
  return result;
};

/**
 * Add a goven value to a list, if the value is neither undefined nor null
 *
 * @param {any[]} list
 * @param value
 * @returns {any[]}
 */
export const addOrIgnore = (list: any[], value: any): any[] => {
  if (!isNullOrUndefined(value)) {
    list.push(value);
  }

  return list;
};

/**
 * Transforms a provided string into the pluralized version
 *
 * @param {string} value
 * @returns {string}
 */
export const pluralize = (value: string): string => {
  if (!value.match(/s$/)) {
    value = `${value}s`;
  }

  return value;

};

/**
 * Transforms a provided string into the singularized version
 *
 * @param {string} value
 * @returns {string}
 */
export const singularize = (value: string): string => {
  if (value.match(/s$/)) {
    value = value.slice(0, value.length - 1);
  }

  return value;

};

/**
 * Given a full qualified resource-uri, the function tries to identify the adressed Resource behind it
 * and returns the name of it.
 *
 * Example:
 *
 * /api/projects => Projects
 * /api/projects/1 => Projects
 * /projects => Projects
 * /projects/1 => Projects
 * /features/1/ => Projects
 * /aircrafts/1/projects => Projects
 *
 * @param {string} resourceId
 * @returns {string}
 */
export const resolveEntityNameByResourceUri = (resourceId: string): string => {
  let entityName = '';
  let resourceParts = resourceId
    .split('/');

  resourceParts = resourceParts.slice(2, resourceParts.length);

  do {
    entityName = resourceParts.pop();
  } while (resourceParts.length > 0 && (entityName && !entityName.match(/[a-zA-Z\-]+/)));

  return entityName;

};

/**
 * Given a full qualified Resource, the function tries to identify the adressed Resource behind it
 * and returns the name of it.
 *
 * @param {{_links: {self: string | {href: string}}}} resource
 * @returns {string}
 */
export const resolveEntityNameByResource = (resource: { _links: { self: string | { href: string } } }): string => {
  let resourceId = resource._links.self;

  if (typeof resourceId === 'object') {
    resourceId = resourceId.href;
  }

  return resolveEntityNameByResourceUri(resourceId);

};

/**
 * Try to determine if the requested resource would provide any children
 *
 * @param {string} resourceId
 * @param schemaService
 * @returns {boolean}
 */
export const resourceCouldHaveChildren = (resourceId: string, schemaService: any): boolean => {
  const entityName = resolveEntityNameByResourceUri(resourceId);
  const entitySchema = schemaService.getForEntityName(entityName);
  let couldHaveRelatedEntities = false;

  if (entitySchema) {
    const propertyNames = Object.keys(entitySchema.properties);

    propertyNames.forEach((propertyName) => {
      if (entitySchema.properties[propertyName].type === GenericObjectTypeInfo.Array) {
        couldHaveRelatedEntities = true;
      }
    });
  }

  return couldHaveRelatedEntities;
};

/**
 * Determines if the resource-id is ending with an ID or not, if the given ID references to a single
 * resource or a list.
 *
 * @param {string} resourceId
 * @returns {boolean}
 */
export const isEntityResource = (resourceId: string): boolean => {
  return !isNullOrUndefined(resourceId.match(/(\/[0-9]+\/*)/));
};

/**
 * Determines if the resource-id is ending with an ID or not, if the given ID references to a single
 * resource or a list.
 *
 * @param {string} resourceId
 * @returns {boolean}
 */
export const isEntityList = (resourceId: string): boolean => {
  return !isEntityResource(resourceId);
};

/**
 * Container for File information in a HttpResponse object
 * Besides the blob-body it provides accessors to retrieve the files real/provided filename
 */
export class FileBlobResponse {
  private regexHeaderFilename = new RegExp(/filename[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/i);

  filename: string;

  get body() {
    return this.response.body;
  }

  constructor(private response: HttpResponse<Blob>) {
    this.filename = this.getFilenameFromHeaders(this.response.headers);
  }

  private getFilenameFromHeaders(headers: HttpHeaders): string {
    const contentDisposition = headers.get('content-disposition');
    const matches = contentDisposition.match(this.regexHeaderFilename);
    let filename = null;

    if (matches) {
      filename = matches[2];
    }

    return filename;
  }
}

/**
 * translates any given string into a camelized string
 *
 * Example:
 * feature-evid-vid-port -> FeatureEvidVidPorts
 *
 * @param anyText
 * @returns {string}
 */
export const camelize = (anyText: string): string => {
  const splitter = '-';
  const joiner = ' ';
  return anyText
    .split(splitter)
    .join(joiner)
    .replace(/\s(.)/g, ($1) => {
      return $1.toUpperCase();
    })
    .replace(/\s/g, '')
    .replace(/^(.)/, ($1) => {
      return $1.toUpperCase();
    });
};

/**
 * Iterating through an object.
 * This can actually be replaced when upgrading to es2017
 *
 * @param object
 * @param callback
 */
export const eachObject = (object: any, callback) => {
  const keyNames = Object.keys(object);
  keyNames.forEach((keyName) => {
    callback(object[keyName], keyName);
  });
};

export const KEY_CODE = {
  escape: 27,
};

/**
 * Returns a random integer value
 *
 * @param min
 * @param max
 * @returns {any}
 */
export const randomInteger = (min= 0, max= 100) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Set the value of propertyName from the model object on the target object.
 *
 * if propertyName does not exists on the model, nothing happens;
 * Does not mutate the original target
 *
 * @param target
 * @param source
 * @param {string} propertyName
 * @returns {any}
 */
export const updateObjectWithProperty = (target: any, source: any, propertyName: string) => {

  const state = {...target};
  if (Object.keys(source).includes(propertyName)) {
    state[propertyName] = source[propertyName];
  }

  return state;
};

// export const dataExtractorFor = <S, T>(source: S): ModelDataExtractor<S, T> => {
//   return new ModelDataExtractor(source);
// }
//
// class ModelDataExtractor<S,T> {
//   constructor(private source: S){
//   }
//
//   getOne(): T {
//     return null;
//   }
//
//   getList(): T[] {
//     return null;
//   }
// }

/**
 * Returns a list with all the extracted children found by the provided key in the given Resource object.
 *
 * @param {Resource} resourceModel
 * @param {string} lookupKey
 * @param {string} propertyName
 * @returns {any[]}
 */
export const extractDataFromProperty = (resourceModel: Resource, lookupKey: string, propertyName = '_links'): any[] => {
  let items: any[] = [];
  if (Object.keys(resourceModel).includes(propertyName)) {
    const linkedResourceKeys = Object.keys(resourceModel[propertyName]);
    if (linkedResourceKeys.includes(lookupKey)) {
      items = resourceModel[propertyName][lookupKey];
    }
  }
  return items;
};

/**
 * Extracting list data from a resource of a generic type
 * @param {T} source
 * @param {string} lookupKey
 * @param {string} propertyName
 * @returns {any[]}
 */
export const extractDataFromModel = <T>(source: T, lookupKey: string, propertyName = '_links'): any|any[] => {
  let items: any[] = [];
  if (Object.keys(source).includes(propertyName)) {
    const linkedResourceKeys = Object.keys(source[propertyName]);
    if (linkedResourceKeys.includes(lookupKey)) {
      items = source[propertyName][lookupKey];
    }
  }
  return items;
};

/**
 * Sort a list by the items label attribute.
 *
 * @param a
 * @param b
 */
export const sortByLabelFunc = (a, b) => {
  return (a.label < b.label ? -1 : (a.label > b.label ? 1 : 0));
};

// export const extractItemFromObject = <T, N>(source: T, item): N => {
//
// }
//
// export const extractItemsFromObject = <T, N>(source: T, lookupKey: string, propertyName = '_links'): N => {
//
// }

/**
 * Build a list of LinkItem values out of a GenericLinkResource.
 * This function is used to fast create list for the PanelList component
 *
 * @param {GenericLinkedResource[]} values
 * @param navigateFactory
 * @returns {LinkItem[]}
 */
export const genericResourceToLinkItem = (values: GenericLinkedResource[], navigateFactory: any): LinkItem[] => {
  return values.map((value: GenericLinkedResource): LinkItem => {
    return {label: value.label, resourceUri: value.href, raw: null, navigate: navigateFactory(value)};
  });
};



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/core/utils/index.ts